import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import {
  delay,
  filter,
  map,
  merge,
  of,
  pairwise,
  startWith,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { AuthToken } from '@shared/signup/models';
import { WebSocketEventsEnum } from '@shared/websocket';
import {
  ServerTimeService,
} from '@tablesready/common-ui';
import { DataLoaderService } from './data-loader.service';
import { AudioService } from '../providers';
import { WaitTimeService } from './wait-time.service';
import { PermissionCheckerService } from './permission-checker.service';
import { LocationSwitcherService } from '@tablesready/frontend-location-switcher';
import {
  AuthAccountService,
  AuthRestaurantService,
  SocketService,
} from '@tablesready/frontend-store';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import {
  AuthService,
  LastLocationIdService,
  TokenService,
} from '@tablesready/frontend-auth';

@Injectable()
export class AuthenticatedInitializer implements OnDestroy {
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly dataLoaderService: DataLoaderService,
    private readonly audioService: AudioService,
    private readonly waitTimeService: WaitTimeService,
    private readonly socketService: SocketService,
    private readonly router: Router,
    private readonly lastLocationIdService: LastLocationIdService,
    private readonly alertsService: AlertsService,
    private readonly tokenService: TokenService,
    private readonly locationSwitcherService: LocationSwitcherService,
    private readonly permissionChecker: PermissionCheckerService,
    private readonly accountService: AuthAccountService,
    private readonly authService: AuthService,
    private readonly serverTimeService: ServerTimeService,
  ) {
    const authUserBuildSubscription = this.authRestaurantService.build()
      .pipe(switchMap(() => this.authRestaurantService.listenToChanges()))
      .subscribe();
    this.subscriptions.add(authUserBuildSubscription);

    const locationChanges = this.authRestaurantService.select()
      .pipe(
        pairwise(),
        filter(([prev, current]) => prev.location?._id !== current.location?._id),
        map(([prev, current]): [boolean, string | undefined, string] => ([
          Boolean(!current.location || current.location && current.location._id !== this.lastLocationIdService.get()),
          current.location?._id,
          prev.account?.id,
        ])),
        tap(([switchedExternally, locationId]) => {
          if (switchedExternally && locationId) {
            this.lastLocationIdService.set(locationId);
          }
        }),
        delay(300),
        map(([switchedExternally, , prevAccId]) => ({
          switchedExternally,
          prevAcc: this.accountService.getById(prevAccId),
        })),
        tap(({ switchedExternally, prevAcc }) => {
          if (prevAcc?.enabled && switchedExternally) {
            this.alertsService.addInfo('Current location is not available. Please contact your administrator.');
          } else if (!prevAcc?.enabled && switchedExternally) {
            this.alertsService.addWarning('This organization’s account is unavailable. Please get in touch with the account administrator or support@tablesready.com if you have any questions');
          }
        }),
      )
      .subscribe();

    this.subscriptions.add(locationChanges);

    const tokenChangesSubscription = merge(
      this.socketService.getEventStream<AuthToken | AuthToken[]>(WebSocketEventsEnum.tokenCreated)
        .pipe(
          map(data => (Array.isArray(data) ? data : [data])),
          tap((tokens) => {
            this.tokenService.add(tokens);
            if (!this.tokenService.getActive().locationId) {
              this.locationSwitcherService.selectLocation(tokens[0].locationId, true);
            }
          }),
        ),
      this.socketService.getEventStream<string>(WebSocketEventsEnum.tokenRevoked)
        .pipe(tap((locationId) => {
          this.tokenService.removeByLocationIds(Array.isArray(locationId) ? locationId : [locationId]);
        })),
    ).subscribe();
    this.subscriptions.add(tokenChangesSubscription);

    const dataLoadedSubscription = this.dataLoaderService.listenToChanges().subscribe();
    this.subscriptions.add(dataLoadedSubscription);

    const restaurantChangedSubscription = this.authRestaurantService.select()
      .pipe(
        startWith(null),
        pairwise(),
        switchMap(([prev, current]) => {
          if (prev === null) {
            this.socketService.connect();

            return this.dataLoaderService.load().pipe(map(() => [prev, current]));
          }

          return of([prev, current]);
        }),
        filter(([prev, current]) => prev !== null && prev?.location?._id !== current.location?._id),
        tap(() => {
          this.socketService.disconnect();
          this.dataLoaderService.reset();
          this.socketService.connect();
        }),
        switchMap(([, current]) => this.dataLoaderService.load().pipe(map(() => current))),
      )
      .subscribe();
    this.subscriptions.add(restaurantChangedSubscription);

    const logoutOnDisabledSubscription = this.authRestaurantService.select()
      .pipe(
        filter(user => !user.enabled),
        take(1),
        tap(() => {
          this.authService.logout();
          this.alertsService.addError('Your account has been disabled by the service administrator. Please contact us if you  have any questions.', '');
        }),
      )
      .subscribe();
    this.subscriptions.add(logoutOnDisabledSubscription);

    this.subscriptions.add(this.permissionChecker.listenChanges().subscribe());
    this.subscriptions.add(this.permissionChecker.isProfileFilled().subscribe());
    this.subscriptions.add(this.audioService.init().subscribe());
    this.subscriptions.add(this.waitTimeService.init().subscribe());

    const authUserRemovedSubscription = this.authRestaurantService.select()
      .pipe(
        filter(value => !Boolean(value._id)),
        tap(() => this.authService.logout()),
      )
      .subscribe();
    this.subscriptions.add(authUserRemovedSubscription);

    const offsetTimeSubscription = this.serverTimeService.offset$
      .pipe(
        tap(offset => {
          // @ts-ignore
          Date.setOffset(offset);
        }),
      )
      .subscribe();
    this.subscriptions.add(offsetTimeSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.dataLoaderService.reset();
    this.socketService.disconnect();
    this.authRestaurantService.reset();
  }
}
